import type {
  DriveAppFile,
  DriveAppFiles,
  DriveAppFilesOptions,
  DriveAppFolderEntry,
  DriveFilePreview,
  DriveFileSelection,
  DriveService,
} from '@blpde/connect';
import {
  createDriveFolder,
  deleteDriveEntry,
  ensurePersonalDriveMount,
  fetchDriveEntry as rawFetchDriveEntry,
  fetchDriveFolder,
  fetchDriveTrash,
  moveDriveEntry,
  restoreDriveTrashEntry,
} from '../api/drive';
import { joinDrivePath, mimeForName } from './paths';

type Entry = {
  name: string;
  path: string;
  isDir: boolean;
  etag?: string;
  documentId?: string;
  revision?: number;
  modifiedAt?: string;
  preview?: { status?: string; text?: string } | null;
};

type Mount = { id: string; name: string };

const fetchDriveEntry = rawFetchDriveEntry as (args: { mountId: string | number | null; path: string; preview?: boolean }) => Promise<unknown>;

/** Names that would leave the app's folder or that Drive would refuse. */
function assertPlainName(name: string): string {
  const trimmed = name.trim();
  if (!trimmed || trimmed === '.' || trimmed === '..' || /[/\\\0]/.test(trimmed)) {
    throw new Error('Drive file name is invalid');
  }
  return trimmed;
}

function isMissing(error: unknown): boolean {
  return Number((error as { status?: unknown } | null | undefined)?.status) === 404;
}

function previewOf(entry: Entry): DriveFilePreview | undefined {
  if (!entry.preview?.status) return undefined;
  return { status: entry.preview.status as DriveFilePreview['status'], text: entry.preview.text ?? '' };
}

function isTaken(error: unknown): boolean {
  const status = Number((error as { status?: unknown } | null | undefined)?.status);
  return status === 409 || status === 412;
}

/**
 * The file-app contract on top of Drive's routes: one folder per app in the
 * person's own mount. Transfers go through the service's `download`/`upload`,
 * so every app shares the same revision handling.
 */
export function createDriveAppFiles(
  { rootFolder, extension }: DriveAppFilesOptions,
  transfer: Pick<DriveService, 'download' | 'upload'>,
): DriveAppFiles {
  const root = assertPlainName(rootFolder);
  const ext = extension.toLowerCase();
  let ready: Promise<Mount> | undefined;

  async function prepare(): Promise<Mount> {
    const mount = await ensurePersonalDriveMount();
    try {
      await fetchDriveEntry({ mountId: mount.id, path: root });
    } catch (error) {
      if (!isMissing(error)) throw error;
      await createDriveFolder({ mountId: mount.id, path: root });
    }
    return { id: String(mount.id), name: mount.name };
  }

  /** A failed first attempt must not stick: the next call tries again. */
  function mount(): Promise<Mount> {
    ready ??= prepare().catch((error) => {
      ready = undefined;
      throw error;
    });
    return ready;
  }

  function relative(path: string): string {
    return path.startsWith(`${root}/`) ? path.slice(root.length + 1) : path;
  }

  function fileOf(target: Mount, entry: Entry): DriveAppFile {
    const preview = previewOf(entry);
    return {
      mountId: target.id,
      mountName: target.name,
      path: entry.path,
      name: entry.name,
      mimeType: mimeForName(entry.name),
      ...(entry.etag ? { etag: entry.etag } : {}),
      ...(entry.documentId ? { documentId: entry.documentId } : {}),
      ...(entry.revision ? { revision: entry.revision } : {}),
      ...(entry.modifiedAt ? { modifiedAt: entry.modifiedAt } : {}),
      ...(preview ? { preview } : {}),
    };
  }

  function withExtension(name: string): string {
    const plain = assertPlainName(name);
    return plain.toLowerCase().endsWith(ext) ? plain : `${plain}${ext}`;
  }

  function numbered(fileName: string, attempt: number): string {
    return attempt === 1 ? fileName : `${fileName.slice(0, -ext.length)} ${attempt}${ext}`;
  }

  return {
    async list(folder = '') {
      const target = await mount();
      const entries = (await (fetchDriveFolder as (args: { mountId: number; path: string; preview: boolean }) => Promise<unknown>)({
        mountId: Number(target.id),
        path: joinDrivePath(root, folder),
        preview: true,
      })) as Entry[];
      const folders: DriveAppFolderEntry[] = [];
      const files: DriveAppFile[] = [];
      for (const entry of entries) {
        if (entry.isDir) folders.push({ path: relative(entry.path), name: entry.name });
        else if (entry.name.toLowerCase().endsWith(ext)) files.push(fileOf(target, entry));
      }
      return { folders, files };
    },
    read: (file) => transfer.download(file),
    async preview(file) {
      const entry = (await fetchDriveEntry({ mountId: Number(file.mountId), path: file.path, preview: true })) as Entry;
      return previewOf(entry) ?? { status: 'unavailable', text: '' };
    },
    async create(name, bytes, folder = '') {
      const target = await mount();
      const dir = joinDrivePath(root, folder);
      const fileName = withExtension(name);
      for (let attempt = 1; attempt <= 50; attempt += 1) {
        const candidate = numbered(fileName, attempt);
        const selection: DriveFileSelection = {
          mountId: target.id,
          mountName: target.name,
          path: joinDrivePath(dir, candidate),
          name: candidate,
          mimeType: mimeForName(candidate),
        };
        try {
          await transfer.upload(selection, bytes, selection.mimeType, { createOnly: true });
          return { ...selection, modifiedAt: new Date().toISOString() };
        } catch (error) {
          if (!isTaken(error)) throw error;
        }
      }
      throw new Error('Drive file name is taken');
    },
    async save(file, bytes) {
      if (!file.etag) throw new Error('Drive revision is unavailable');
      await transfer.upload(file, bytes, file.mimeType, { etag: file.etag });
    },
    async rename(file, name) {
      const target = await mount();
      const fileName = withExtension(name);
      const dir = file.path.includes('/') ? file.path.slice(0, file.path.lastIndexOf('/')) : '';
      const path = joinDrivePath(dir, fileName);
      await moveDriveEntry({ mountId: Number(target.id), from: file.path, to: path, ifMatch: file.etag ?? '' });
      return fileOf(target, (await fetchDriveEntry({ mountId: Number(target.id), path })) as Entry);
    },
    async createFolder(name) {
      const target = await mount();
      const plain = assertPlainName(name);
      await createDriveFolder({ mountId: Number(target.id), path: joinDrivePath(root, plain) });
      return { path: plain, name: plain };
    },
    async trash(file) {
      await deleteDriveEntry({ mountId: Number(file.mountId), path: file.path, ifMatch: file.etag ?? '' });
    },
    async trashed() {
      const target = await mount();
      const prefix = `${root}/`;
      return (await fetchDriveTrash())
        .filter(
          (entry) =>
            String(entry.mountId) === target.id &&
            !entry.isDir &&
            entry.originalPath.replace(/^\/+/, '').startsWith(prefix) &&
            entry.name.toLowerCase().endsWith(ext) &&
            (entry.status ?? 'active') === 'active',
        )
        .map((entry) => ({
          id: entry.id,
          mountId: target.id,
          name: entry.name,
          deletedAt: entry.deletedAt,
          etag: entry.etag,
        }));
    },
    async restore(entry) {
      await restoreDriveTrashEntry({ mountId: entry.mountId, trashId: entry.id, ifMatch: entry.etag });
    },
  };
}
